import React from "react";
import { Text, Stack, rem, Box, Group } from "@mantine/core";
import {
  IconDeviceDesktop,
  IconWorld,
  IconSpeakerphone,
  IconLink,
  IconShoppingBag,
  IconCreditCard,
  IconReceipt,
  IconClipboard,
  IconBrowser,
  IconCoins,
  IconHelp,
} from "@tabler/icons-react";
import {
  BusinessGoalsData,
  BUSINESS_GOALS_OPTIONS,
  QUESTION_KEYS,
  GoalOptionConfig,
} from "../types/onboarding";
import styles from "./BusinessGoalsStepContent.module.css";

const iconMap: Record<string, React.ElementType> = {
  IconDeviceDesktop,
  IconWorld,
  IconSpeakerphone,
  IconLink,
  IconShoppingBag,
  IconCreditCard,
  IconReceipt,
  IconClipboard,
  IconCoins,
  IconBrowser,
  IconHelp,
};

interface GoalCardProps {
  goal: GoalOptionConfig;
  isSelected: boolean;
  onToggle: () => void;
}

function GoalCard({ goal, isSelected, onToggle }: GoalCardProps) {
  const IconComponent = iconMap[goal.icon] || IconHelp;

  return (
    <Group
      gap="md"
      align="flex-start"
      className={`${styles.goalCard} ${isSelected ? styles.goalCardSelected : ""}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{ cursor: "pointer" }}
    >
      <Box className={styles.goalIcon}>
        <IconComponent
          style={{ width: rem(24), height: rem(24), color: "#101820" }}
        />
      </Box>
      <Stack gap={2} className={styles.goalText}>
        <Text size="md" fw={400} className={styles.goalTitle}>
          {goal.title}
        </Text>
        <Text size="sm" c="dimmed" className={styles.goalDescription}>
          {goal.description}
        </Text>
      </Stack>
    </Group>
  );
}

interface BusinessGoalsStepContentProps {
  data: BusinessGoalsData;
  onChange: (
    data: Partial<BusinessGoalsData>,
    questionKey?: string,
    questionValue?: string,
    answerValue?: string,
  ) => void;
}

export default function BusinessGoalsStepContent({
  data,
  onChange,
}: BusinessGoalsStepContentProps) {
  const handleGoalToggle = (goalValue: string) => {
    const currentGoals = data.selectedGoals;
    let newGoals: string[];

    if (currentGoals.includes(goalValue)) {
      newGoals = currentGoals.filter((g) => g !== goalValue);
    } else {
      newGoals = [...currentGoals, goalValue];
    }

    onChange(
      { selectedGoals: newGoals },
      QUESTION_KEYS.BUSINESS_GOALS,
      "Business goals",
      newGoals.join(","),
    );
  };

  return (
    <Stack gap="md" className={styles.container}>
      <Stack gap="xs" className={styles.goalsList}>
        {BUSINESS_GOALS_OPTIONS.map((goal) => (
          <GoalCard
            key={goal.value}
            goal={goal}
            isSelected={data.selectedGoals.includes(goal.value)}
            onToggle={() => handleGoalToggle(goal.value)}
          />
        ))}
      </Stack>
    </Stack>
  );
}
